import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { timeFormatter } from "../../../../constants/constants";
import { SelectOptionType } from "../../../../types/types";
import { InvoiceTime } from "../../../time/InvoiceTime";
import { isDueDateBeforeInvoiceDate } from "../../billingDates.utils";
import {
  BillingServiceOption,
  DiscountType,
  InvoiceFormInitialValues,
  InvoiceFormItem,
  InvoiceTermOption,
  TaxRateOption,
} from "./invoice-form.types";

export type InvoiceDraft = {
  invoiceNo: number;
  customerId: string | null;
  invoiceCustomersIds: string[];
  invoiceDate: string;
  dueDate: string;
  term: string;
  selectedUserId: string | null;
  isIncludeTax: boolean;
  discount: string;
  discountType: DiscountType;
  taxRateId: string | null;
  notes: string;
  items: Array<{
    service: string;
    description: string;
    qty: number;
    rate: string;
    amount: number;
    invoiceType: string;
    assignedUserId: string | null;
    workTypeId: string | null;
    workId: string | null;
    duration: number;
    taxCodeId: string | null;
  }>;
};

export const parseMoney = (value: string | number | undefined | null) =>
  Number(String(value ?? "0").replace(/,/g, "")) || 0;

export const formatMoney = (value: number) =>
  (value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const getTaxMultiplier = (taxRateValue?: TaxRateOption | null) =>
  1 +
  (taxRateValue?.taxRates?.reduce(
    (sum, taxRate) => sum + (taxRate.taxRate || 0),
    0,
  ) ?? 0) /
    100;

export const calculateDueDateByTerm = (
  terms: InvoiceTermOption[] | undefined,
  termId: string,
  invoiceDate: Date,
) => {
  const termName = terms?.find((item) => item.id === termId)?.termName;

  switch (termName) {
    case "Due on Receipt":
      return invoiceDate;

    case "Net 60":
      return moment(invoiceDate).add(60, "days").toDate();

    case "Net 30":
      return moment(invoiceDate).add(30, "days").toDate();

    case "Net 15":
      return moment(invoiceDate).add(15, "days").toDate();

    default:
      return null;
  }
};

/**
 * Due date after the person changes Terms or the invoice date (QA batch 10):
 * the term's date when the term sets one, else the current due date (moved up
 * to the invoice date if it would be earlier). Only called from those two
 * changes, so opening Edit keeps the saved due date.
 */
export const dueDateAfterTermsChange = (
  terms: InvoiceTermOption[] | undefined,
  termId: string,
  invoiceDate: Date,
  dueDate: Date,
) =>
  calculateDueDateByTerm(terms, termId, invoiceDate) ??
  (isDueDateBeforeInvoiceDate(invoiceDate, dueDate) ? invoiceDate : dueDate);

/**
 * Terms after the person picks a due date by hand (QA batch 11): a term that
 * gives exactly that date stays; otherwise the firm's "Custom" term when it has
 * one, else no term ("" = the "Custom date" choice), so Terms never says
 * "Due on Receipt" next to a due date that isn't the invoice date.
 */
export const termAfterDueDateChange = (
  terms: InvoiceTermOption[] | undefined,
  termId: string,
  invoiceDate: Date,
  dueDate: Date,
) => {
  const termDate = calculateDueDateByTerm(terms, termId, invoiceDate);
  const isCustomTerm =
    !!termId && !termDate && terms?.some((item) => item.id === termId);
  if (isCustomTerm) return termId;
  if (termDate && moment(termDate).isSame(dueDate, "day")) return termId;
  return (
    terms?.find((item) => item.termName?.trim().toLowerCase() === "custom")
      ?.id ?? ""
  );
};

/** Whether the firm's terms include a "Custom" one (else the form offers "Custom date"). */
export const hasCustomTerm = (terms: InvoiceTermOption[] | undefined) =>
  !!terms?.some((item) => item.termName?.trim().toLowerCase() === "custom");

export const totalRate = (value: string) => {
  const hours = Number(value.split("h").slice(0, -1));
  const minutes = Number(value?.split("h")?.pop()?.replace("m", "")) / 60;

  return hours + minutes;
};

export const createDefaultInvoiceItem = (
  service?: BillingServiceOption,
): InvoiceFormItem => ({
  clientRowId: uuidv4(),
  service: service?.id || "",
  description: service?.description || "",
  qty: 1,
  rate: service?.rate?.toString() || "0",
  amount: service?.rate || 0,
  invoiceType: "invoiceItem",
  assignedUserId: null,
  workId: null,
  workTypeId: null,
  taxCodeId: "",
  duration: 0,
});

export const mapBillableTimeToInvoiceItem = (
  item: InvoiceTime,
  fallbackService?: BillingServiceOption,
): InvoiceFormItem => {
  const hoursQty = +totalRate(timeFormatter(item.duration)).toFixed(2);
  const hourlyRate = item.assignedUser?.hourlyRate || 0;

  return {
    clientRowId: item.id,
    id: item.id,
    service: fallbackService?.id || "",
    description: item.work.description,
    qty: hoursQty,
    rate: hourlyRate.toString(),
    isWork: true,
    workType: item.work.service.name,
    workName: item.work.name,
    dueDate: item.work.dueDate,
    assignedUser: `${item.assignedUser?.userProfile.firstName} ${item.assignedUser?.userProfile.lastName}`,
    invoiceType: "billedItem",
    assignedUserId: item.assignedUserId,
    workId: item.work.id,
    workTypeId: item.work.service.id,
    duration: item.duration,
    taxCodeId: "",
    amount: hoursQty * hourlyRate,
  };
};

export const buildInvoiceDraft = (params: {
  invoiceNo: number;
  currentContact: SelectOptionType | null;
  invoiceCustomers: SelectOptionType[];
  invoiceDate: Date;
  dueDate: Date;
  term: string;
  selectedUserId: string | null;
  isIncludeTax: boolean;
  discount: string;
  discountType: DiscountType;
  taxRateId: string | null;
  notes: string;
  data: InvoiceFormItem[];
}): InvoiceDraft => {
  const {
    invoiceNo,
    currentContact,
    invoiceCustomers,
    invoiceDate,
    dueDate,
    term,
    selectedUserId,
    isIncludeTax,
    discount,
    discountType,
    taxRateId,
    notes,
    data,
  } = params;

  return {
    invoiceNo,
    customerId: currentContact?.value ?? null,
    invoiceCustomersIds: invoiceCustomers.map((customer) => customer.value),
    invoiceDate: invoiceDate.toISOString(),
    dueDate: dueDate.toISOString(),
    term,
    selectedUserId,
    isIncludeTax,
    discount,
    discountType,
    taxRateId,
    notes,
    items: data.map((item) => ({
      service: item.service,
      description: item.description,
      qty: item.qty,
      rate: item.rate,
      amount: item.amount,
      invoiceType: item.invoiceType,
      assignedUserId: item.assignedUserId,
      workTypeId: item.workTypeId,
      workId: item.workId,
      duration: item.duration,
      taxCodeId: item.taxCodeId || null,
    })),
  };
};

/**
 * Tax code a new invoice starts with (Roadmap 1791): the firm's default
 * (Settings > Billing > Tax Rate), else the first active one as before.
 */
export const pickDefaultTaxRate = <T extends { isDefault?: boolean }>(
  taxRates: T[] | undefined,
): T | null =>
  taxRates?.find((item) => item.isDefault) ?? taxRates?.[0] ?? null;

/**
 * Whether the invoice number needs the "already in use" warning on save
 * (Roadmap 1816). Duplicates stay allowed, like QuickBooks (owner's rule);
 * the person is only warned. When editing, keeping the invoice's own number
 * is never a conflict.
 */
export const shouldCheckInvoiceNumber = (
  mode: "create" | "edit",
  invoiceNo: number,
  originalInvoiceNo: number,
) => mode === "create" || invoiceNo !== originalInvoiceNo;

export const isInvoiceNumberInUse = (
  invoices: Array<{ invoiceNo?: number | null }> | undefined,
  invoiceNo: number,
) =>
  !!invoiceNo &&
  (invoices ?? []).some((invoice) => Number(invoice.invoiceNo) === invoiceNo);

/**
 * Tracked-time lines are sent with their Time id (QA C1/C3, 2026-10-22): the
 * server gives the line that id, marks the time billed and frees it again when
 * the invoice or the line is deleted. Other lines keep no id on a new invoice.
 */
export const invoiceLineId = (
  item: Pick<InvoiceFormItem, "id" | "invoiceType">,
) => (item.invoiceType === "billedItem" && item.id ? item.id : undefined);

/** The "View work" records for tracked time on an invoice (id = Time id). */
export const billedWorkRecords = (
  billedWorks: InvoiceTime[],
  invoiceId: string,
) =>
  billedWorks.map((work) => {
    const hourlyRate = work.assignedUser?.hourlyRate || 0;
    const durationRate = totalRate(timeFormatter(work.duration));

    return {
      id: work.id,
      description: work.work?.name || "",
      duration: work.duration,
      rate: hourlyRate,
      amount: hourlyRate * durationRate,
      serviceId: work.work?.serviceId || work.work?.service?.id || "",
      invoiceId,
      workTypeId: work.work?.service?.id || "",
      assignedUserId: work.assignedUserId,
    };
  });

/** Typed text in an invoice date box that isn't a date (BILL-030). */
export const INVALID_DATE =
  "That isn't a date. Type it like Oct 22, 2026 or 2026/10/22, or pick it in the calendar.";

export const MAX_INVOICE_RATE = 1_000_000;
export const MAX_INVOICE_QUANTITY = 100_000;

/**
 * Invoice amount rules (QA batch 17, BILL-034 / BILL-041; the server checks the
 * same in `InvoiceAmounts`): rate at most $1,000,000, quantity at most 100,000,
 * no negative discount, a % discount at most 100.
 */
export const invoiceAmountProblem = (params: {
  items: Array<{ rate: string | number; qty: number }>;
  discount: string;
  discountType: DiscountType;
}): string | null => {
  const discount = parseMoney(params.discount);
  if (discount < 0) return "The discount can't be negative.";
  if (params.discountType === "%" && discount > 100)
    return "A discount can't be more than 100%.";
  if (params.items.some((item) => Math.abs(parseMoney(item.rate)) > MAX_INVOICE_RATE))
    return "A rate can be at most $1,000,000.";
  if (params.items.some((item) => Math.abs(Number(item.qty) || 0) > MAX_INVOICE_QUANTITY))
    return "A quantity can be at most 100,000.";
  return null;
};

/**
 * Ask ProCharted "invoice …" (2026-10-26): the New invoice form starting from
 * the proposed dates, terms, notes and lines (each line on its billing
 * service, else the first one). Unbilled time is added by the form itself
 * (`defaultWorkId` / `defaultAllTime`). Nothing is saved until Save Invoice.
 */
export const aiInvoiceValues = (
  values: InvoiceFormInitialValues,
  ai: {
    invoiceDate: string | null;
    dueDate: string | null;
    termId: string;
    notes: string;
    lines: {
      description: string;
      quantity: number;
      rate: number;
      serviceId: string;
    }[];
  },
  services: BillingServiceOption[],
): InvoiceFormInitialValues => {
  const day = (value: string | null) =>
    value ? moment(value, "YYYY-MM-DD", true) : null;
  const invoiceDay = day(ai.invoiceDate);
  const dueDay = day(ai.dueDate);
  const lines = ai.lines.map((line) => {
    const service =
      services.find((item) => item.id === line.serviceId) || services[0];
    const quantity = line.quantity > 0 ? line.quantity : 1;
    return {
      ...createDefaultInvoiceItem(service),
      // "ai-" = proposed line: kept when the form adds unbilled time.
      clientRowId: `ai-${uuidv4()}`,
      description: line.description || service?.description || "",
      qty: quantity,
      rate: String(line.rate),
      amount: +(quantity * line.rate).toFixed(2),
    };
  });
  return {
    ...values,
    invoiceDate: invoiceDay?.isValid()
      ? invoiceDay.toDate()
      : values.invoiceDate,
    dueDate: dueDay?.isValid() ? dueDay.toDate() : values.dueDate,
    // A proposed due date without a term = "Custom date" ("").
    term: ai.termId || (dueDay?.isValid() ? "" : values.term),
    notes: ai.notes || values.notes,
    items: lines.length ? lines : values.items,
  };
};
